import { exec } from 'node:child_process';
import { platform } from 'node:os';
import { serve } from '@hono/node-server';
import { Command } from 'commander';
import { createApp } from '../../server/app.js';
import { createServices, destroyServices } from '../../services/index.js';
import type { GlobalOptions } from '../program.js';
import type { ServerType } from '@hono/node-server';
import type { AddressInfo } from 'node:net';

/**
 * Start the Hono server, trying the preferred port first.
 * If the preferred port is in use, falls back to an OS-assigned free port.
 * Uses the `serve` callback's `AddressInfo` to report the actual bound port.
 */
export function startServer(
  fetch: Parameters<typeof serve>[0]['fetch'],
  preferredPort: number,
  host: string
): Promise<{ server: ServerType; port: number }> {
  return new Promise((resolve, reject) => {
    const server = serve({ fetch, port: preferredPort, hostname: host }, (info: AddressInfo) => {
      resolve({ server, port: info.port });
    });

    server.on('error', (err: NodeJS.ErrnoException) => {
      if (err.code === 'EADDRINUSE') {
        // Preferred port taken — let the OS pick a free one
        const retryServer = serve({ fetch, port: 0, hostname: host }, (info: AddressInfo) => {
          resolve({ server: retryServer, port: info.port });
        });
        retryServer.on('error', reject);
      } else {
        reject(err);
      }
    });
  });
}

export function createServeCommand(getOptions: () => GlobalOptions): Command {
  return new Command('serve')
    .description('Start HTTP API server for programmatic search access')
    .option('-p, --port <port>', 'Port to listen on (reads from config if not specified)')
    .option(
      '--host <host>',
      'Bind address (reads from config if not specified, use 0.0.0.0 for all interfaces)'
    )
    .option('--open', 'Open the admin UI in the default browser after starting')
    .action(async (options: { port?: string; host?: string; open?: boolean }) => {
      const globalOpts = getOptions();
      const services = await createServices(
        globalOpts.config,
        globalOpts.dataDir,
        globalOpts.projectRoot
      );

      // Load config for defaults
      const appConfig = await services.config.load();
      const app = createApp(services);

      // Use config defaults, CLI flags override
      let preferredPort: number;
      if (options.port !== undefined) {
        preferredPort = parseInt(options.port, 10);
        if (Number.isNaN(preferredPort)) {
          throw new Error(`Invalid value for --port: "${options.port}" is not a valid integer`);
        }
      } else {
        preferredPort = appConfig.server.port;
      }
      const host = options.host ?? appConfig.server.host;

      const { server, port } = await startServer(app.fetch, preferredPort, host);
      if (port !== preferredPort) {
        console.log(`Port ${String(preferredPort)} in use, using ${String(port)}`);
      }
      const url = `http://${host}:${String(port)}`;
      console.log(`Starting server on ${url}`);

      if (options.open === true) {
        const openCmd =
          platform() === 'darwin' ? 'open' : platform() === 'win32' ? 'start' : 'xdg-open';
        exec(`${openCmd} ${url}`);
      }

      // Graceful shutdown: close HTTP server, then cleanup services.
      // Do NOT call process.exit() - let the event loop drain naturally
      // so native code (lancedb, ONNX) can clean up without mutex corruption.
      let shuttingDown = false;
      const shutdown = (): void => {
        if (shuttingDown) return; // Prevent double-shutdown
        shuttingDown = true;
        server.close(() => {
          void destroyServices(services);
        });
      };

      process.on('SIGINT', shutdown);
      process.on('SIGTERM', shutdown);
    });
}
